import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    let attemptCount = 0;

    const checkStatus = async () => {
      if (attemptCount >= 5) {
        setStatus('failed');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (cancelled) return;

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(response.data);
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else {
          attemptCount++;
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error checking payment status:', error);
        attemptCount++;
        setTimeout(checkStatus, 2000);
      }
    };

    checkStatus();

    return () => { cancelled = true; };
  }, [sessionId]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
          <h2 className="text-2xl font-sans font-semibold mb-2">Processing Your Payment</h2>
          <p className="text-stone-600 font-light">Please wait while we confirm your booking...</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-3xl font-sans font-semibold mb-4">Payment Failed</h2>
          <p className="text-stone-600 font-light mb-8">
            We couldn't process your payment. Please try again or contact us for assistance.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/properties')}
              className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-sm transition-colors font-semibold"
            >
              Browse Properties
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="w-full border border-stone-300 hover:bg-stone-50 text-stone-700 py-3 rounded-sm transition-colors"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-sm border border-stone-200 overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-4xl font-sans font-semibold mb-2">Booking Confirmed!</h1>
            <p className="text-white/90 font-light text-lg">
              Thank you for choosing InFox Suites
            </p>
          </div>

          {/* Booking Details */}
          <div className="p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-sans font-semibold mb-4">What's Next?</h2>
              <div className="space-y-4 text-stone-700">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-primary font-semibold">1</span>
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold mb-1">Check Your Email</p>
                    <p className="text-sm text-stone-600 font-light">
                      We've sent a confirmation email with your booking details and check-in instructions.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-primary font-semibold">2</span>
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold mb-1">Save the Details</p>
                    <p className="text-sm text-stone-600 font-light">
                      You'll receive check-in instructions, access codes, and property information closer to your arrival date.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-primary font-semibold">3</span>
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold mb-1">Questions?</p>
                    <p className="text-sm text-stone-600 font-light">
                      Contact us via WhatsApp at +30 6936 547 334 or email infoxsuites@info-x.gr
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="bg-stone-50 p-6 rounded-sm border border-stone-200 mb-8">
              <h3 className="font-semibold text-stone-900 mb-2">📄 Cancellation Policy</h3>
              <p className="text-sm text-stone-600 font-light leading-relaxed">
                Free cancellation if you cancel at least <strong>21 days</strong> before check-in. 
                If you cancel within 21 days of arrival, the 35% deposit will be retained as a cancellation fee.
              </p>
            </div>

            {/* Payment Details */}
            {paymentDetails && (
              <div className="bg-primary/5 p-6 rounded-sm border border-primary/20 mb-8">
                <h3 className="font-semibold text-stone-900 mb-3">Payment Confirmation</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-600">Amount Paid</span>
                    <span className="font-semibold">
                      €{(paymentDetails.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Status</span>
                    <span className="font-semibold text-green-600">Paid</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-sm transition-colors font-semibold"
                data-testid="back-home-button"
              >
                Back to Home
              </button>
              <button
                onClick={() => navigate('/properties')}
                className="w-full border border-stone-300 hover:bg-stone-50 text-stone-700 py-4 rounded-sm transition-colors"
              >
                Browse More Properties
              </button>
            </div>

            {/* Review CTA */}
            <div className="mt-8 text-center p-6 bg-gradient-to-r from-stone-50 to-stone-100 rounded-sm">
              <p className="text-sm text-stone-600 font-light mb-3">
                Enjoyed your stay? We'd love to hear from you!
              </p>
              <a
                href="https://g.page/r/CXtmWrVeJHhfEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-primary hover:underline font-semibold"
              >
                Leave us a review →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;
